import React from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../assets/logo.png';
import useV2Auth from '../hooks/useV2Auth';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import V2LegacyReturnButton from './V2LegacyReturnButton';
import '../admin/admin-ui.css';

const adminNav = [
  { label: 'Аналитика', to: '/admin' },
  { label: 'Ошибки парсера', to: '/admin/parser-errors' },
  { labelKey: 'admin_mining_upload' as const, to: '/admin/mining-upload' },
  { label: 'Рефералы', to: '/admin/referrals' },
  { label: 'Пользователи', to: '/users' },
];

function resolveAdminBreadcrumb(pathname: string, miningUploadLabel: string, userId?: string) {
  if (pathname.startsWith('/users/') && userId) {
    return (
      <>
        Admin <span>/</span> <Link to="/users">Пользователи</Link> <span>/</span>{' '}
        <strong>#{userId}</strong>
      </>
    );
  }

  if (pathname.startsWith('/users')) {
    return (
      <>
        Admin <span>/</span> <strong>Пользователи</strong>
      </>
    );
  }

  if (pathname.startsWith('/admin/referrals')) {
    return (
      <>
        Admin <span>/</span> <strong>Рефералы</strong>
      </>
    );
  }

  if (pathname.startsWith('/admin/parser-errors')) {
    return (
      <>
        Admin <span>/</span> <strong>Ошибки парсера</strong>
      </>
    );
  }

  if (pathname.startsWith('/admin/mining-upload')) {
    return (
      <>
        Admin <span>/</span> <strong>{miningUploadLabel}</strong>
      </>
    );
  }

  return (
    <>
      Admin <span>/</span> <strong>Аналитика</strong>
    </>
  );
}

const V2AdminLayout: React.FC = () => {
  const { t } = useTranslation('translation');
  const { user } = useV2Auth();
  useIsAdmin();
  const location = useLocation();
  const { id: userId } = useParams();
  const accountInitial = (user?.email?.trim()?.[0] ?? 'A').toUpperCase();

  return (
    <div className="v2-theme v2-admin-layout" data-page="admin">
      <aside className="sidebar" aria-label="Админ-панель">
        <Link className="brand" to="/statistics" aria-label="Fish Hunter — статистика">
          <img src={logo} alt="Fish Hunter" />
        </Link>
        <p className="eyebrow sidebar-caption">АДМИНИСТРИРОВАНИЕ</p>
        <nav className="side-nav v2-admin-side-nav" aria-label="Админ-навигация">
          {adminNav.map((item) => {
            const isActive =
              item.to === '/admin'
                ? location.pathname === item.to
                : location.pathname.startsWith(item.to);

            return (
              <Link
                key={item.to}
                to={item.to}
                className={isActive ? 'active' : undefined}
                aria-current={isActive ? 'page' : undefined}
              >
                {item.labelKey ? t(item.labelKey) : item.label}
              </Link>
            );
          })}
        </nav>
        <div className="sidebar-bottom">
          <Link className="account-link" to="/statistics">
            <span className="avatar">{accountInitial}</span>
            <span>
              К приложению
              <small>{user?.email ?? 'Fish Hunter'}</small>
            </span>
            <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </aside>

      <div className="workspace">
        <header className="workspace-header v2-admin-workspace-header">
          <Link
            className="brand mobile-brand"
            to="/statistics"
            aria-label="Fish Hunter — статистика"
          >
            <img src={logo} alt="Fish Hunter" />
          </Link>
          <p className="breadcrumbs">
            {resolveAdminBreadcrumb(location.pathname, t('admin_mining_upload'), userId)}
          </p>
        </header>
        <nav className="v2-admin-mobile-nav" aria-label="Админ-навигация">
          {adminNav.map((item) => {
            const isActive =
              item.to === '/admin'
                ? location.pathname === item.to
                : location.pathname.startsWith(item.to);

            return (
              <Link
                key={item.to}
                to={item.to}
                className={isActive ? 'active' : undefined}
                aria-current={isActive ? 'page' : undefined}
              >
                {item.labelKey ? t(item.labelKey) : item.label}
              </Link>
            );
          })}
        </nav>
        <main id="main">
          <div className="v2-legacy-bridge">
            <Outlet />
          </div>
        </main>
        <footer className="workspace-footer">
          <span>
            <span className="status-dot"></span> FISH HUNTER <span className="muted">/ Admin</span>
          </span>
          <V2LegacyReturnButton />
        </footer>
      </div>
    </div>
  );
};

export default V2AdminLayout;
